/**
 * TransactionModal Component
 *
 * Modal form for creating and editing manual balance history transactions.
 * Supports 7 manual transaction types (all except trade, which is system-reserved).
 * Handles sign logic per type, converts decimal input to integer storage via
 * toStorageValue(), and constructs UTC timestamps from date/time inputs.
 *
 * Add mode: blank form, calls addTransaction().
 * Edit mode: pre-populated from editTransaction prop (sign-aware amount display),
 * transaction type read-only, calls update().
 *
 * Follows AccountCreateModal pattern: reset on open, centralized validation,
 * close only on full success (write + refresh).
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { parseDate, Time } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  NumberInput,
  Select,
  SelectItem,
  DatePicker,
  TimeInput,
  RadioGroup,
  Radio,
  Alert,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import { TRANSACTION_TYPE_DISPLAY_MAP } from '@/constants';
import { balanceHistoryService } from '@/services/BalanceHistoryService';
import type { BalanceHistory } from '@/types/schema/BalanceHistory';
import { toUTC, getTodayInTimezone } from '@/utils/dateHelpers';
import { toStorageValue, toDisplayValue, getPrecisionForCurrency } from '@/utils/moneyHelpers';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Manual transaction types in deliberate UX order (excludes 'trade' — system-reserved) */
const MANUAL_TRANSACTION_TYPES: BalanceHistory['transactionType'][] = [
  'deposit',
  'withdrawal',
  'adjustment',
  'fee',
  'dividend',
  'interest',
  'other',
];

/** Types where the stored amount is always positive */
const FIXED_POSITIVE_TYPES = new Set<BalanceHistory['transactionType']>([
  'deposit',
  'dividend',
  'interest',
]);

/** Types where the stored amount is always negative */
const FIXED_NEGATIVE_TYPES = new Set<BalanceHistory['transactionType']>([
  'withdrawal',
  'fee',
]);

/** Types that use a direction control (increase/decrease) */
const DIRECTION_CONTROLLED_TYPES = new Set<BalanceHistory['transactionType']>([
  'adjustment',
  'other',
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  accountId: string;
  effectiveCurrency: string;
  userTimezone: string;
  /** When provided, modal operates in edit mode with pre-populated form */
  editTransaction?: BalanceHistory;
}

interface TransactionFormData {
  transactionType: BalanceHistory['transactionType'] | '';
  amount: number | undefined;
  date: DateValue;
  time: Time;
  direction: 'increase' | 'decrease';
  description: string;
  category: string;
  externalReference: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Zero-pad a number to 2 digits */
const pad = (n: number): string => String(n).padStart(2, '0');

/** Build initial form state with current date/time */
function createInitialFormData(userTimezone: string): TransactionFormData {
  const todayStr = getTodayInTimezone(userTimezone);
  const now = new Date();

  return {
    transactionType: '',
    amount: undefined,
    date: parseDate(todayStr),
    time: new Time(now.getHours(), now.getMinutes()),
    direction: 'increase',
    description: '',
    category: '',
    externalReference: '',
  };
}

/** Parse a UTC ISO timestamp into local DateValue + Time for the user's timezone */
function parseTimestampForEdit(
  timestamp: string,
  timezone: string
): { date: DateValue; time: Time } {
  const utcDate = new Date(timestamp);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(utcDate);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '0';

  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  const hour = Number(get('hour'));
  const minute = Number(get('minute'));

  return {
    date: parseDate(`${year}-${pad(month)}-${pad(day)}`),
    time: new Time(hour, minute),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TransactionModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  accountId,
  effectiveCurrency,
  userTimezone,
  editTransaction,
}) => {
  const isEditMode = !!editTransaction;

  // Form state
  const [formData, setFormData] = useState<TransactionFormData>(
    () => createInitialFormData(userTimezone)
  );
  const [typeError, setTypeError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const precision = getPrecisionForCurrency(effectiveCurrency);

  // Reset form when modal opens — pre-populate in edit mode
  useEffect(() => {
    if (isOpen) {
      if (editTransaction) {
        const { date, time } = parseTimestampForEdit(
          editTransaction.timestamp,
          userTimezone
        );
        const displayAmount = toDisplayValue(
          Math.abs(editTransaction.amount),
          editTransaction.amountPrecision
        );
        const initialDirection = DIRECTION_CONTROLLED_TYPES.has(
          editTransaction.transactionType
        )
          ? editTransaction.amount < 0
            ? 'decrease'
            : 'increase'
          : 'increase';

        setFormData({
          transactionType: editTransaction.transactionType,
          amount: displayAmount,
          date,
          time,
          direction: initialDirection as 'increase' | 'decrease',
          description: editTransaction.description ?? '',
          category: editTransaction.category ?? '',
          externalReference: editTransaction.externalReference ?? '',
        });
      } else {
        setFormData(createInitialFormData(userTimezone));
      }
      setTypeError(null);
      setAmountError(null);
      setSubmitError(null);
    }
  }, [isOpen, userTimezone, editTransaction]);

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  // Whether the current type uses a direction control
  const showDirection =
    formData.transactionType !== '' &&
    DIRECTION_CONTROLLED_TYPES.has(formData.transactionType);

  // -------------------------------------------------------------------------
  // Field change handlers
  // -------------------------------------------------------------------------

  const handleTypeChange = (value: BalanceHistory['transactionType']): void => {
    setFormData((prev) => ({ ...prev, transactionType: value }));
    if (typeError) setTypeError(null);
    if (submitError) setSubmitError(null);
  };

  const handleAmountChange = useCallback(
    (value: number | undefined): void => {
      setFormData((prev) => ({ ...prev, amount: value }));
      if (amountError) setAmountError(null);
      if (submitError) setSubmitError(null);
    },
    [amountError, submitError]
  );

  const handleDateChange = (value: DateValue | null): void => {
    if (value) {
      setFormData((prev) => ({ ...prev, date: value }));
      if (submitError) setSubmitError(null);
    }
  };

  const handleTimeChange = (value: unknown): void => {
    if (value && value instanceof Time) {
      setFormData((prev) => ({ ...prev, time: value }));
      if (submitError) setSubmitError(null);
    }
  };

  const handleDirectionChange = (value: string): void => {
    setFormData((prev) => ({
      ...prev,
      direction: value as 'increase' | 'decrease',
    }));
    if (submitError) setSubmitError(null);
  };

  const handleStringChange = (
    field: 'description' | 'category' | 'externalReference',
    value: string
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  };

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    let isValid = true;

    if (!formData.transactionType) {
      setTypeError('Transaction type is required');
      isValid = false;
    } else {
      setTypeError(null);
    }

    if (formData.amount === undefined || formData.amount <= 0) {
      setAmountError('Amount must be greater than 0');
      isValid = false;
    } else {
      setAmountError(null);
    }

    return isValid;
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    // Type narrowing — validated above
    const txType = formData.transactionType as BalanceHistory['transactionType'];
    const rawAmount = formData.amount as number;

    // Convert decimal display value to integer storage value
    const integerAmount = toStorageValue(rawAmount, precision);
    if (integerAmount === undefined) {
      setSubmitError('Failed to convert amount');
      return;
    }

    // Apply sign based on type and direction
    let signedAmount: number;
    if (FIXED_POSITIVE_TYPES.has(txType)) {
      signedAmount = integerAmount;
    } else if (FIXED_NEGATIVE_TYPES.has(txType)) {
      signedAmount = -integerAmount;
    } else {
      // Direction-controlled
      signedAmount =
        formData.direction === 'decrease' ? -integerAmount : integerAmount;
    }

    // Build UTC timestamp from date + time
    const dateStr = `${formData.date.year}-${pad(formData.date.month)}-${pad(formData.date.day)}`;
    const timeStr = `${pad(formData.time.hour)}:${pad(formData.time.minute)}:00`;
    const timestamp = toUTC(`${dateStr}T${timeStr}`, userTimezone);

    // Optional field spreads (exactOptionalPropertyTypes compliance)
    const optionalFields = {
      ...(formData.description.trim()
        ? { description: formData.description.trim() }
        : {}),
      ...(formData.category.trim()
        ? { category: formData.category.trim() }
        : {}),
      ...(formData.externalReference.trim()
        ? { externalReference: formData.externalReference.trim() }
        : {}),
    };

    try {
      setIsSubmitting(true);

      if (editTransaction) {
        // Edit mode — update existing transaction
        await balanceHistoryService.update(editTransaction.id, {
          amount: signedAmount,
          amountPrecision: precision,
          balancePrecision: precision,
          timestamp,
          ...optionalFields,
        });
      } else {
        // Add mode — create new transaction
        await balanceHistoryService.addTransaction({
          accountId,
          timestamp,
          transactionType: txType,
          amount: signedAmount,
          amountPrecision: precision,
          balancePrecision: precision,
          ...optionalFields,
        });
      }

      // Refresh parent table
      await onSaved();

      // Close only on full success
      setIsSubmitting(false);
      handleClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? 'update' : 'add'} transaction`;
      setSubmitError(errorMessage);
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      placement="top-center"
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Edit Transaction' : 'Add Transaction'}</ModalHeader>

        <ModalBody className="gap-4">
          {/* Transaction Type */}
          <Select
            label="Transaction Type"
            placeholder="Select type"
            selectedKeys={
              formData.transactionType ? [formData.transactionType] : []
            }
            onSelectionChange={(keys) => {
              if (keys === 'all') return;
              const selected = Array.from(keys)[0] as string | undefined;
              if (selected) {
                handleTypeChange(
                  selected as BalanceHistory['transactionType']
                );
              }
            }}
            isRequired
            isDisabled={isEditMode}
            isInvalid={!!typeError}
            errorMessage={typeError}
          >
            {MANUAL_TRANSACTION_TYPES.map((type) => (
              <SelectItem key={type} textValue={TRANSACTION_TYPE_DISPLAY_MAP[type]}>
                {TRANSACTION_TYPE_DISPLAY_MAP[type]}
              </SelectItem>
            ))}
          </Select>

          {/* Amount */}
          <NumberInput
            label="Amount"
            placeholder="0.00"
            value={formData.amount}
            onValueChange={handleAmountChange}
            minValue={0}
            isRequired
            isInvalid={!!amountError}
            errorMessage={amountError}
            formatOptions={{
              minimumFractionDigits: precision,
              maximumFractionDigits: precision,
            }}
          />

          {/* Direction (only for adjustment / other) */}
          {showDirection && (
            <RadioGroup
              label="Direction"
              orientation="horizontal"
              value={formData.direction}
              onValueChange={handleDirectionChange}
            >
              <Radio value="increase">Increase</Radio>
              <Radio value="decrease">Decrease</Radio>
            </RadioGroup>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              label="Date"
              value={formData.date}
              onChange={handleDateChange}
              isRequired
            />
            <TimeInput
              label="Time"
              value={formData.time}
              onChange={handleTimeChange}
            />
          </div>

          {/* Description */}
          <Input
            label="Description"
            placeholder="Optional description"
            value={formData.description}
            onValueChange={(value) => handleStringChange('description', value)}
            className="[&_input]:text-base"
          />

          {/* Category */}
          <Input
            label="Category"
            placeholder="Optional category"
            value={formData.category}
            onValueChange={(value) => handleStringChange('category', value)}
            className="[&_input]:text-base"
          />

          {/* External Reference */}
          <Input
            label="External Reference"
            placeholder="Optional reference ID"
            value={formData.externalReference}
            onValueChange={(value) =>
              handleStringChange('externalReference', value)
            }
            className="[&_input]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={handleClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            {isEditMode ? 'Save Changes' : 'Add Transaction'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
